// src/hooks/useNotifications.tsx
import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';

export function useNotifications(userId: string) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [receivedTotal, setReceivedTotal] = useState(0);
  const [receivedAccepted, setReceivedAccepted] = useState(0);
  const [receivedPendingCount, setReceivedPendingCount] = useState(0);
  const [myShareCount, setMyShareCount] = useState(0);
  const [myAcceptedCount, setMyAcceptedCount] = useState(0);
  const [sharedPendingCount, setSharedPendingCount] = useState(0);

  // 用於避免同一 Session 內 15 秒輪詢重複觸發 Toast
  const notifiedIdsRef = useRef<Set<string>>(new Set());

  // 標記已讀的函式
  const markAsRead = useCallback(async (notificationId?: string) => {
    if (!userId) return;
    try {
      const response = await fetch('/api/notifications', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId,
        },
        body: JSON.stringify({ notificationId: notificationId || null }),
      });

      if (response.ok) {
        // 重新重新整理列表以更新狀態
        fetchNotifications();
      }
    } catch (error) {
      console.warn('標記已讀失敗:', error);
    }
  }, [userId]);

  // 獲取通知與分享統計
  const fetchNotifications = useCallback(async () => {
    if (!userId) return;

    try {
      // 1. 獲取通知
      const notifRes = await fetch('/api/notifications?limit=10', {
        headers: { 'X-User-Id': userId },
      });

      if (notifRes.ok) {
        const data = await notifRes.json();
        const fetchedNotifs = data.notifications || [];
        setNotifications(fetchedNotifs);
        setUnreadCount(data.unreadCount || 0);

        // 🎯 核心優化：過濾出未讀且「本次登入 Session 未彈窗過」的通知
        const unreadNotifs = fetchedNotifs.filter(
          (n: any) => !n.is_read && !notifiedIdsRef.current.has(n.id)
        );

        if (unreadNotifs.length > 0) {
          const latest = unreadNotifs[0];

          // 紀錄至 Ref，避免 15 秒輪詢重複觸發
          notifiedIdsRef.current.add(latest.id);

          // 顯示 Toast 提示
          toast(latest.title, {
            duration: 5000,
            icon: '🔔',
            style: {
              borderRadius: '12px',
              background: '#1a202c',
              color: '#fff',
            },
          });

          // 🎯 關鍵：彈窗後自動發起請求將該筆通知轉為「已讀」，資料庫持久化後下次登入絕不重複彈出
          markAsRead(latest.id);
        }
      }

      // 2. 獲取分享列表統計
      const [receivedRes, sharedRes] = await Promise.all([
        fetch('/api/notes/share-list?type=received', {
          headers: { 'X-User-Id': userId },
        }),
        fetch('/api/notes/share-list?type=shared', {
          headers: { 'X-User-Id': userId },
        }),
      ]);

      if (receivedRes.ok) {
        const data = await receivedRes.json();
        const pending = data.filter((s: any) => s.status === 'pending').length;
        const accepted = data.filter((s: any) => s.status === 'accepted').length;
        setReceivedTotal(data.length);
        setReceivedAccepted(accepted);
        setReceivedPendingCount(pending);
      }

      if (sharedRes.ok) {
        const data = await sharedRes.json();
        const accepted = data.filter((s: any) => s.status === 'accepted').length;
        const pending = data.filter((s: any) => s.status === 'pending').length;
        setMyShareCount(data.length);
        setMyAcceptedCount(accepted);
        setSharedPendingCount(pending);
      }
    } catch (error) {
      console.warn('獲取通知失敗:', error);
    }
  }, [userId, markAsRead]);

  // 定時輪詢與初始化
  useEffect(() => {
    if (!userId) return;

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);

    return () => clearInterval(interval);
  }, [userId, fetchNotifications]);

  // 手動清空記憶體紀錄 (供登出或切換帳號時使用)
  const clearNotifiedIds = () => {
    notifiedIdsRef.current.clear();
  };

  return {
    notifications,
    unreadCount,
    receivedTotal,
    receivedAccepted,
    receivedPendingCount,
    myShareCount,
    myAcceptedCount,
    sharedPendingCount,
    markAsRead,
    refresh: fetchNotifications,
    clearNotifiedIds,
  };
}